import * as THREE from 'three';

const clamp = THREE.MathUtils.clamp;
const ease = (value) => THREE.MathUtils.smoothstep(value, 0, 1);

// Curved energy blades cut across the entire vault, then leave an expanding
// shockwave, flying seal fragments, and a burst of sparks at the broken lock.
export const createTreasureSlashScene = (chest, glowTexture, rarityColor) => {
  const root = new THREE.Group();
  root.position.set(0, 0.76, 1.28);
  chest.add(root);
  const bladeShape = new THREE.Shape();
  bladeShape.moveTo(-2.8, -0.24);
  bladeShape.quadraticCurveTo(-0.2, 1.0, 2.8, 0.18);
  bladeShape.quadraticCurveTo(0.1, 0.36, -2.8, -0.24);
  const bladeGeometry = new THREE.ShapeGeometry(bladeShape, 28);
  const blades = [-0.62, 0.7].map((angle, index) => {
    const blade = new THREE.Group();
    blade.rotation.z = angle;
    blade.position.z = index * 0.04;
    [
      { color: 0x773aff, scale: 1.8, opacity: 0.28 },
      { color: rarityColor, scale: 1.1, opacity: 0.7 },
      { color: 0xeaffff, scale: 0.32, opacity: 1 },
    ].forEach(({ color, scale, opacity }) => {
      const streak = new THREE.Mesh(
        bladeGeometry,
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
          side: THREE.DoubleSide,
        })
      );
      streak.scale.y = scale;
      streak.userData.opacity = opacity;
      blade.add(streak);
    });
    root.add(blade);
    return blade;
  });
  const rings = [0, 1].map((index) => {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.52, index ? 0.009 : 0.025, 6, 80),
      new THREE.MeshBasicMaterial({
        color: index ? rarityColor : 0xd9ffff,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    ring.position.z = 0.06;
    root.add(ring);
    return ring;
  });
  const flash = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: glowTexture,
      color: 0xe7dbff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );
  root.add(flash);
  const sparkCount = 80;
  const positions = new Float32Array(sparkCount * 3);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const sparks = new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      color: 0xffe9b5,
      size: 0.085,
      map: glowTexture,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );
  root.add(sparks);
  const fragmentGeometry = new THREE.TetrahedronGeometry(0.075);
  const fragmentMaterial = new THREE.MeshBasicMaterial({ color: rarityColor });
  const fragments = Array.from({ length: 12 }, (_, index) => {
    const fragment = new THREE.Mesh(fragmentGeometry, fragmentMaterial);
    fragment.userData.angle = index * 2.39996;
    root.add(fragment);
    return fragment;
  });

  return {
    update({ time, sliceAt, impactAt, reduceMotion }) {
      root.visible =
        !reduceMotion && time >= sliceAt - 200 && time < impactAt + 1100;
      if (!root.visible) return 0;
      blades.forEach((blade, index) => {
        const elapsed = time - sliceAt - index * 125;
        const sweep = ease(elapsed / 230);
        const fade = ease(elapsed / 40) * (1 - ease((elapsed - 240) / 430));
        blade.visible = elapsed >= 0 && fade > 0;
        blade.position.x = -0.6 + sweep * 0.8;
        blade.position.y = (index ? -1 : 1) * (1 - sweep) * 0.3;
        blade.scale.set(0.08 + sweep, 1 - ease((elapsed - 240) / 430) * 0.8, 1);
        blade.children.forEach((streak) => {
          streak.material.opacity = streak.userData.opacity * fade;
        });
      });
      const impact = Math.exp(-Math.pow((time - impactAt) / 115, 2));
      flash.material.opacity = impact * 0.95;
      flash.scale.setScalar(0.6 + impact * 4.4);
      rings.forEach((ring, index) => {
        const elapsed = time - impactAt - index * 85;
        const p = clamp(elapsed / 720, 0, 1);
        ring.visible = elapsed >= 0 && p < 1;
        ring.scale.setScalar(0.3 + ease(p) * (index ? 8 : 6.5));
        ring.material.opacity = (1 - p) ** 2 * 0.72;
      });
      const burst = clamp((time - impactAt + 30) / 950, 0, 1);
      sparks.visible = time >= impactAt - 30 && burst < 1;
      sparks.material.opacity = (1 - burst) ** 1.5;
      for (let index = 0; index < sparkCount; index += 1) {
        const angle = index * 2.39996;
        const distance = burst * (1.2 + (index % 7) * 0.3);
        positions[index * 3] = Math.cos(angle) * distance;
        positions[index * 3 + 1] =
          Math.sin(angle) * distance - burst * burst * 1.6;
        positions[index * 3 + 2] = burst * (0.2 + (index % 5) * 0.22);
      }
      geometry.attributes.position.needsUpdate = true;
      fragments.forEach((fragment, index) => {
        const distance = burst * (0.9 + (index % 4) * 0.45);
        const angle = fragment.userData.angle;
        fragment.visible = time >= impactAt && burst < 1;
        fragment.position.set(
          Math.cos(angle) * distance,
          Math.sin(angle) * distance + burst * 0.5 - burst * burst * 1.5,
          burst * (0.3 + (index % 3) * 0.3)
        );
        fragment.rotation.set(burst * 9, angle + burst * 6, burst * 4);
        fragment.scale.setScalar(1 - burst * 0.8);
      });
      return impact;
    },
  };
};
